import { useState, useEffect, useRef } from 'react'
import { Container, Card, Button, Modal, Form, Badge, ListGroup, Table } from 'react-bootstrap'
import { useAuth } from '../contexts/AuthContext'
import { useProfile } from '../contexts/ProfileContext'
import { useToast } from '../contexts/ToastContext'

function Profiles() {
  const { user } = useAuth()
  const {
    profiles,
    activeProfile,
    createProfile,
    updateProfile,
    deleteProfile,
    cloneProfile,
    switchProfile,
    refreshProfiles,
    getSubordinateSearchHistory,
    setParentPin,
    removeParentPin
  } = useProfile()
  const { showSuccess, showError } = useToast()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [showPinModal, setShowPinModal] = useState(false)
  const [editingProfile, setEditingProfile] = useState(null)
  const [deletingProfile, setDeletingProfile] = useState(null)
  const [viewingHistoryProfile, setViewingHistoryProfile] = useState(null)
  const [pinProfile, setPinProfile] = useState(null)
  const [searchHistory, setSearchHistory] = useState([])
  const [historyFilter, setHistoryFilter] = useState(null)
  const [pinDigits, setPinDigits] = useState(['', '', '', '', ''])
  const pinInputRefs = [useRef(), useRef(), useRef(), useRef(), useRef()]

  const [formData, setFormData] = useState({
    name: '',
    birth_year: '',
    email_address: '',
    age_restrict_results: true
  })

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    refreshProfiles()
  }, [])

  const handleCreateProfile = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await createProfile({
        name: formData.name,
        birth_year: formData.birth_year ? parseInt(formData.birth_year) : null,
        email_address: formData.email_address || null,
        age_restrict_results: formData.age_restrict_results,
        parent_profile_id: activeProfile?.id || null
      })

      showSuccess('Profile created successfully!')
      setShowCreateModal(false)
      resetForm()
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to create profile')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await updateProfile(editingProfile.id, {
        name: formData.name,
        birth_year: formData.birth_year ? parseInt(formData.birth_year) : null,
        email_address: formData.email_address || null,
        age_restrict_results: formData.age_restrict_results
      })

      showSuccess('Profile updated successfully!')
      setShowEditModal(false)
      setEditingProfile(null)
      resetForm()
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteProfile = async () => {
    if (!deletingProfile) return

    setLoading(true)

    try {
      await deleteProfile(deletingProfile.id)
      showSuccess('Profile deleted successfully!')
      setShowDeleteModal(false)
      setDeletingProfile(null)
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to delete profile')
    } finally {
      setLoading(false)
    }
  }

  const handleCloneProfile = async (profileId) => {
    setLoading(true)

    try {
      await cloneProfile(profileId)
      showSuccess('Profile cloned successfully!')
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to clone profile')
    } finally {
      setLoading(false)
    }
  }

  const openHistoryModal = async (profile) => {
    setViewingHistoryProfile(profile)
    setHistoryFilter(null)
    setLoading(true)

    try {
      const history = await getSubordinateSearchHistory(profile.id)
      setSearchHistory(history)
      setShowHistoryModal(true)
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to load search history')
    } finally {
      setLoading(false)
    }
  }

  const handleHistoryFilterChange = async (subordinateId) => {
    if (!viewingHistoryProfile) return
    setHistoryFilter(subordinateId)
    setLoading(true)

    try {
      const history = await getSubordinateSearchHistory(viewingHistoryProfile.id, subordinateId)
      setSearchHistory(history)
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to load search history')
    } finally {
      setLoading(false)
    }
  }

  const openPinModal = (profile) => {
    setPinProfile(profile)
    setPinDigits(['', '', '', '', ''])
    setShowPinModal(true)
    setTimeout(() => pinInputRefs[0].current?.focus(), 100)
  }

  const handlePinDigitChange = (index, value) => {
    if (!/^\d?$/.test(value)) return
    const newPinDigits = [...pinDigits]
    newPinDigits[index] = value
    setPinDigits(newPinDigits)
    if (value && index < 4) {
      pinInputRefs[index + 1].current?.focus()
    }
  }

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      pinInputRefs[index - 1].current?.focus()
    }
  }

  const handleSetPin = async (e) => {
    e.preventDefault()
    if (!pinProfile) return

    const fullPin = pinDigits.join('')
    if (fullPin.length !== 5) {
      showError('Please enter all 5 digits')
      return
    }

    setLoading(true)

    try {
      await setParentPin(pinProfile.id, fullPin)
      showSuccess('PIN set successfully!')
      setShowPinModal(false)
      setPinProfile(null)
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to set PIN')
    } finally {
      setLoading(false)
    }
  }

  const handleRemovePin = async (profile) => {
    setLoading(true)

    try {
      await removeParentPin(profile.id)
      showSuccess('PIN removed successfully!')
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to remove PIN')
    } finally {
      setLoading(false)
    }
  }

  // Get subordinate profiles for a parent
  const getSubordinatesForProfile = (profileId) => {
    return profiles.filter(p => p.parent_profile_id === profileId)
  }

  const openCreateModal = () => {
    resetForm()
    setShowCreateModal(true)
  }

  const openEditModal = (profile) => {
    setEditingProfile(profile)
    setFormData({
      name: profile.name,
      birth_year: profile.birth_year || '',
      email_address: profile.email_address || '',
      age_restrict_results: profile.age_restrict_results
    })
    setShowEditModal(true)
  }

  const openDeleteModal = (profile) => {
    setDeletingProfile(profile)
    setShowDeleteModal(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      birth_year: '',
      email_address: '',
      age_restrict_results: true
    })
  }

  const canCreateProfile = user && (!user.premium ? profiles.length < 2 : true)
  const showPinManagement = editingProfile && !editingProfile.parent_profile_id && getSubordinatesForProfile(editingProfile.id).length > 0

  return (
    <Container className="mt-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h2>Manage Profiles</h2>
        </div>
        <Button
          variant="primary"
          onClick={openCreateModal}
          disabled={!canCreateProfile}
          className="flex-shrink-0"
        >
          Create Profile
        </Button>
      </div>


      <ListGroup>
        {/* Render parent profiles (profiles without a parent) */}
        {profiles.filter(p => !p.parent_profile_id).map((profile) => {
          const childProfiles = getSubordinatesForProfile(profile.id)

          return (
            <div key={profile.id}>
              {/* Parent Profile */}
              <ListGroup.Item>
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2">
                  <div className="flex-grow-1">
                    <h5 className="mb-1">
                      {profile.name}
                      {activeProfile?.id === profile.id && (
                        <Badge bg="primary" className="ms-2">Active</Badge>
                      )}
                      {profile.age_restrict_results && (
                        <Badge bg="info" className="ms-2">Age Filter</Badge>
                      )}
                      {childProfiles.length > 0 && (
                        <Badge bg="success" className="ms-2">{childProfiles.length} child profile{childProfiles.length > 1 ? 's' : ''}</Badge>
                      )}
                    </h5>
                    <div className="text-muted small">
                      {profile.age && <span>Age: {profile.age} • </span>}
                      {profile.email_address && <span>{profile.email_address} • </span>}
                      Created: {new Date(profile.created).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-1 gap-md-2 mt-2 mt-lg-0">
                    {activeProfile?.id !== profile.id && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => switchProfile(profile)}
                      >
                        Activate
                      </Button>
                    )}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => openEditModal(profile)}
                    >
                      Edit
                    </Button>
                    {childProfiles.length > 0 && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => openHistoryModal(profile)}
                      >
                        History
                      </Button>
                    )}
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleCloneProfile(profile.id)}
                      disabled={!canCreateProfile}
                    >
                      Clone
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => openDeleteModal(profile)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </ListGroup.Item>

              {/* Child Profiles - indented */}
              {childProfiles.map((child) => (
                <ListGroup.Item key={child.id} className="bg-light" style={{ paddingLeft: '2.5rem' }}>
                  <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2">
                    <div className="flex-grow-1">
                      <h6 className="mb-1">
                        <span className="text-muted me-2">└</span>
                        {child.name}
                        {activeProfile?.id === child.id && (
                          <Badge bg="primary" className="ms-2">Active</Badge>
                        )}
                        {child.age_restrict_results && (
                          <Badge bg="info" className="ms-2">Age Filter</Badge>
                        )}
                        {child.has_parent_pin && (
                          <Badge bg="warning" text="dark" className="ms-2">PIN Protected</Badge>
                        )}
                      </h6>
                      <div className="text-muted small" style={{ marginLeft: '1.25rem' }}>
                        {child.age && <span>Age: {child.age} • </span>}
                        {child.email_address && <span>{child.email_address} • </span>}
                        Created: {new Date(child.created).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-1 gap-md-2 mt-2 mt-lg-0">
                      {activeProfile?.id !== child.id && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => switchProfile(child)}
                        >
                          Activate
                        </Button>
                      )}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => openEditModal(child)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleCloneProfile(child.id)}
                        disabled={!canCreateProfile}
                      >
                        Clone
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => openDeleteModal(child)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </div>
          )
        })}
      </ListGroup>

      {profiles.length === 0 && (
        <Card className="text-center py-5">
          <Card.Body>
            <h4>No Profiles Yet</h4>
            <p className="text-muted">Create your first profile to get started!</p>
            <Button variant="primary" onClick={openCreateModal}>
              Create Profile
            </Button>
          </Card.Body>
        </Card>
      )}

      <p className="text-center mt-4" style={{ color: '#888' }}>
        {user?.premium ? (
          <>Premium account - more profiles</>
        ) : !canCreateProfile ? (
          <>Upgrade to premium for more profiles</>
        ) : (
          <>Free account - {profiles.length} of 2 profiles used</>
        )}
      </p>

      {/* Create Profile Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Profile</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateProfile}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Birth Year</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g., 2010"
                value={formData.birth_year}
                onChange={(e) => setFormData({...formData, birth_year: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email (optional)</Form.Label>
              <Form.Control
                type="email"
                value={formData.email_address}
                onChange={(e) => setFormData({...formData, email_address: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Enable age-appropriate content filtering"
                checked={formData.age_restrict_results}
                onChange={(e) => setFormData({...formData, age_restrict_results: e.target.checked})}
              />
            </Form.Group>

          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Profile'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Profile Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Profile</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateProfile}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Birth Year</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g., 2010"
                value={formData.birth_year}
                onChange={(e) => setFormData({...formData, birth_year: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email (optional)</Form.Label>
              <Form.Control
                type="email"
                value={formData.email_address}
                onChange={(e) => setFormData({...formData, email_address: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Enable age-appropriate content filtering"
                checked={formData.age_restrict_results}
                onChange={(e) => setFormData({...formData, age_restrict_results: e.target.checked})}
              />
            </Form.Group>

            {/* PIN Management for Parent Profiles */}
            {showPinManagement && (
              <>
                <hr />
                <p>
                  Set a PIN for your account. Use it as a quick way to switch from any child profile back to this main profile.
                </p>
                {getSubordinatesForProfile(editingProfile.id).map(child => (
                  <div key={child.id} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                    <div>
                      <strong>{child.name}</strong>
                      {child.has_parent_pin && (
                        <Badge bg="warning" text="dark" className="ms-2">PIN Set</Badge>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant={child.has_parent_pin ? "outline-warning" : "outline-primary"}
                        size="sm"
                        onClick={() => {
                          setShowEditModal(false)
                          openPinModal(child)
                        }}
                      >
                        {child.has_parent_pin ? 'Change PIN' : 'Set PIN'}
                      </Button>
                      {child.has_parent_pin && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemovePin(child)}
                          disabled={loading}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Profile'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Profile Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the profile <strong>{deletingProfile?.name}</strong>?
          This will permanently delete all scores, categories, and trust relationships for this profile.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteProfile} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete Profile'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Search History Modal */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Search History for Child Profiles of {viewingHistoryProfile?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Form.Label>Filter by Child Profile</Form.Label>
            <Form.Select
              value={historyFilter || ''}
              onChange={(e) => handleHistoryFilterChange(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">All Child Profiles</option>
              {viewingHistoryProfile && getSubordinatesForProfile(viewingHistoryProfile.id).map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name}</option>
              ))}
            </Form.Select>
          </div>

          {loading ? (
            <p className="text-center">Loading search history...</p>
          ) : searchHistory.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Profile</th>
                  <th>Keywords</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Results</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {searchHistory.map(query => (
                  <tr key={query.id}>
                    <td>
                      <Badge bg="secondary">{query.profile_name}</Badge>
                    </td>
                    <td><strong>{query.keyword}</strong></td>
                    <td>{query.scorable_type || 'All'}</td>
                    <td>{query.category || 'All'}</td>
                    <td>{query.result_count}</td>
                    <td>{new Date(query.time_started).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-center text-muted">No search history found.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Set/Change PIN Modal */}
      <Modal show={showPinModal} onHide={() => setShowPinModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {pinProfile?.has_parent_pin ? 'Change' : 'Set'} PIN for {pinProfile?.name}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSetPin}>
          <Modal.Body className="text-center">
            <p className="text-muted mb-4">
              Enter a 5-digit PIN. This PIN will be required when switching from {pinProfile?.name}'s profile to the parent profile.
            </p>
            <div className="d-flex justify-content-center gap-2 mb-3">
              {pinDigits.map((digit, index) => (
                <Form.Control
                  key={index}
                  ref={pinInputRefs[index]}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(index, e)}
                  style={{
                    width: '50px',
                    height: '60px',
                    fontSize: '24px',
                    textAlign: 'center',
                    fontWeight: 'bold'
                  }}
                  disabled={loading}
                  autoComplete="off"
                />
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPinModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading || pinDigits.some(d => d === '')}
            >
              {loading ? 'Saving...' : (pinProfile?.has_parent_pin ? 'Change PIN' : 'Set PIN')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}

export default Profiles
